/**
 * Filters: which deals make the cut.
 *
 * Everything that narrows the report sits in this one panel without a scroll: the period, the
 * four workspace filters and the search are fixed rows, and a filter on a particular column is a
 * chip that says what it keeps. The chip opens its editor over the report, so a column filter
 * costs the panel one chip rather than one form.
 */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'
import { Filter, X } from 'lucide-react'
import { COLUMN_DEFS, COLUMN_MAP } from '../columns'
import { isActiveColumnFilter } from '../reporting'
import { optionClass } from '../studioUi'
import { STAGES } from '../types'
import type { ColumnFilterValue, ColumnId, FilterState, ReportConfig } from '../types'
import { DeckPanel, PopoverPanel } from './controls'
import { denseField, denseFieldAuto, denseLabel, useDismiss } from './controlStyles'
import { describeColumnFilter } from './studioFilters'

type Props = {
  active: boolean
  config: ReportConfig
  setConfig: Dispatch<SetStateAction<ReportConfig>>
  baseFilters: FilterState
  setBaseFilters: Dispatch<SetStateAction<FilterState>>
  assetClasses: string[]
  assignees: string[]
  years: string[]
  /** The distinct values of each enum column, for its filter's checklist. */
  enumOptions: Map<ColumnId, string[]>
}

export function FiltersPanel({
  active,
  config,
  setConfig,
  baseFilters,
  setBaseFilters,
  assetClasses,
  assignees,
  years,
  enumOptions,
}: Props) {
  // The column whose filter editor is open: the one just added, or the chip clicked.
  const [editingId, setEditingId] = useState<ColumnId | null>(null)
  const closeEditor = useCallback(() => setEditingId(null), [])
  const editorRef = useDismiss<HTMLLIElement>(editingId != null, closeEditor)

  const setColumnFilter = (id: ColumnId, patch: Partial<ColumnFilterValue> | null) =>
    setConfig((current) => {
      const next = { ...current.columnFilters }
      if (patch === null) delete next[id]
      else next[id] = { ...next[id], ...patch }
      return { ...current, columnFilters: next }
    })

  const filteredIds = Object.keys(config.columnFilters) as ColumnId[]
  const activeCount =
    filteredIds.filter((id) => isActiveColumnFilter(config.columnFilters[id])).length +
    (config.dateFrom || config.dateTo ? 1 : 0)

  return (
    <DeckPanel
      id="filters"
      title="Filters"
      icon={<Filter className="h-3.5 w-3.5" />}
      active={active}
      meta={<span className="tabular-nums">{activeCount === 0 ? 'none applied' : `${activeCount} applied`}</span>}
    >
      <div>
        <div className={denseLabel}>Date received</div>
        <div className="mt-1 grid grid-cols-2 gap-1.5">
          <input
            type="date"
            value={config.dateFrom}
            onChange={(event) => setConfig((current) => ({ ...current, dateFrom: event.target.value }))}
            className={denseField}
            aria-label="Report period from"
          />
          <input
            type="date"
            value={config.dateTo}
            onChange={(event) => setConfig((current) => ({ ...current, dateTo: event.target.value }))}
            className={denseField}
            aria-label="Report period to"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-1.5 gap-y-1.5">
        {(
          [
            ['stage', 'Stage', STAGES as readonly string[]],
            ['assetClass', 'Asset class', assetClasses],
            ['assignee', 'Assignee', assignees],
            ['year', 'Year', years],
          ] as const
        ).map(([key, label, options]) => (
          <label key={key} className="block min-w-0">
            <span className={denseLabel}>{label}</span>
            <select
              value={baseFilters[key]}
              onChange={(event) => setBaseFilters((current) => ({ ...current, [key]: event.target.value }))}
              className={`${denseField} mt-0.5`}
            >
              <option className={optionClass} value="All">
                All
              </option>
              {options.map((option) => (
                <option className={optionClass} key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <label className="block">
        <span className="sr-only">Search deals</span>
        <input
          value={baseFilters.search}
          onChange={(event) => setBaseFilters((current) => ({ ...current, search: event.target.value }))}
          placeholder="Search deals, sponsors, brokers…"
          className={denseField}
        />
      </label>

      <div>
        <div className="flex items-center gap-2">
          <span className={denseLabel}>Column filters</span>
          <select
            value=""
            onChange={(event) => {
              const id = event.target.value as ColumnId | ''
              if (!id) return
              setColumnFilter(id, {})
              setEditingId(id)
            }}
            className={`${denseFieldAuto} ml-auto w-44`}
            aria-label="Choose a column to filter"
          >
            <option className={optionClass} value="">
              Filter a column…
            </option>
            {COLUMN_DEFS.filter((column) => !(column.id in config.columnFilters)).map((column) => (
              <option className={optionClass} key={column.id} value={column.id}>
                {column.label}
              </option>
            ))}
          </select>
        </div>
        {filteredIds.length === 0 ? (
          <p className="mt-1 text-[10px] text-stone-600">No column filters. Pick a column to constrain it.</p>
        ) : (
          <ul className="mt-1 flex flex-wrap gap-1">
            {filteredIds.map((id) => {
              const def = COLUMN_MAP.get(id)
              if (!def) return null
              const filter = config.columnFilters[id]
              const open = editingId === id
              return (
                <li key={id} ref={open ? editorRef : undefined} className="relative">
                  <span className="inline-flex items-center rounded border border-amber-300/25 bg-amber-300/[0.06] text-[10px] text-amber-100">
                    <button
                      type="button"
                      onClick={() => setEditingId(open ? null : id)}
                      aria-expanded={open}
                      aria-label={`Edit ${def.label} filter`}
                      className="max-w-[12rem] truncate py-0.5 pl-1.5 pr-1 hover:text-white"
                    >
                      {def.label}: <span className="text-amber-200/80">{describeColumnFilter(def, filter)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setColumnFilter(id, null)
                        if (open) setEditingId(null)
                      }}
                      className="rounded p-0.5 text-amber-200/60 hover:text-red-300"
                      aria-label={`Clear ${def.label} filter`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                  {open ? (
                    <PopoverPanel label={`${def.label} filter`} className="w-60">
                      <ColumnFilterEditor
                        id={id}
                        filter={filter ?? {}}
                        options={enumOptions.get(id) ?? []}
                        onChange={(patch) => setColumnFilter(id, patch)}
                      />
                    </PopoverPanel>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </DeckPanel>
  )
}

function ColumnFilterEditor({
  id,
  filter,
  options,
  onChange,
}: {
  id: ColumnId
  filter: ColumnFilterValue
  options: string[]
  onChange: (patch: Partial<ColumnFilterValue>) => void
}) {
  const def = COLUMN_MAP.get(id)
  if (!def) return null
  if (def.kind === 'enum') {
    const chosen = filter.values ?? []
    return (
      <div className="max-h-48 space-y-1 overflow-y-auto pr-1">
        {options.map((option) => {
          const selected = chosen.includes(option)
          return (
            <label key={option} className="flex items-center gap-2 text-[11px] text-stone-300">
              <input
                type="checkbox"
                checked={selected}
                onChange={() =>
                  onChange({ values: selected ? chosen.filter((value) => value !== option) : [...chosen, option] })
                }
              />
              {option}
            </label>
          )
        })}
        <div className="text-[9px] text-stone-600">Nothing checked = all values.</div>
      </div>
    )
  }
  if (def.kind === 'text') {
    return (
      <input
        value={filter.contains ?? ''}
        onChange={(event) => onChange({ contains: event.target.value })}
        placeholder="Contains…"
        className={denseField}
        aria-label={`${def.label} contains`}
        autoFocus
      />
    )
  }
  if (def.kind === 'number') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={filter.min ?? ''}
          onChange={(event) => onChange({ min: event.target.value === '' ? null : Number(event.target.value) })}
          placeholder="Min"
          className={denseField}
          aria-label={`${def.label} minimum`}
        />
        <input
          type="number"
          value={filter.max ?? ''}
          onChange={(event) => onChange({ max: event.target.value === '' ? null : Number(event.target.value) })}
          placeholder="Max"
          className={denseField}
          aria-label={`${def.label} maximum`}
        />
      </div>
    )
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <input
        type="date"
        value={filter.from ?? ''}
        onChange={(event) => onChange({ from: event.target.value })}
        className={denseField}
        aria-label={`${def.label} from`}
      />
      <input
        type="date"
        value={filter.to ?? ''}
        onChange={(event) => onChange({ to: event.target.value })}
        className={denseField}
        aria-label={`${def.label} to`}
      />
    </div>
  )
}
